import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';

/**
 * Het startscherm van deze bewoner.
 *
 * ── TWEE LAGEN, DOOR DE SERVER SAMENGEVOEGD ─────────────────────────────────
 *
 * De beheerder schikt een standaard per site; de bewoner zet knoppen uit, verplaatst ze
 * of voegt een eigen lamp of sfeer toe. Wat hier binnenkomt is het resultaat van die
 * twee — de app rekent niets uit, want een tweede kopie van die samenvoeging zou vroeg
 * of laat iets anders tonen dan wat er opgeslagen staat.
 *
 * ── DE VOORKEUR HOORT BIJ DE PERSOON, NIET BIJ HET TOESTEL ──────────────────
 *
 * Tot nu toe stond de indeling in `localStorage`. Een tabletwissel, een reset of een
 * browser die zijn opslag opruimt, en de bewoner begon opnieuw. Voor iemand die zijn
 * knoppen op positie en tast gebruikt is dat geen kleinigheid. Nu staat hij bij de
 * persoon.
 *
 * ── EN HIJ MAG NOG NIET BESTAAN ─────────────────────────────────────────────
 *
 * `available: false` betekent: dit endpoint is er nog niet (de API is nog niet
 * uitgerold). De aanroeper valt dan terug op de oude, lokale lijst. Dat is bewust geen
 * foutmelding: een tablet die "startscherm niet beschikbaar" toont terwijl er gewoon nog
 * een deploy moet gebeuren, is erger dan een tablet die de vorige knoppen laat staan.
 */
export interface HomeLayoutItem {
  key: string;
  kind: 'page' | 'output' | 'scene';
  label: string | null;
  icon: string | null;
  color: string | null;
  order: number;
  enabled: boolean;
  path?: string;
  outputId?: number;
  outputName?: string;
  sceneId?: number;
  capability?: 'switch' | 'dimmer' | 'scene' | 'input';
  value?: boolean | number | null;
}

export interface HomeLayout {
  available: boolean;
  /** `site` = door een beheerder geschikt, `builtin` = de ingebouwde lijst. */
  source: 'site' | 'builtin' | null;
  personalised: boolean;
  items: HomeLayoutItem[];
}

const LEEG: HomeLayout = { available: false, source: null, personalised: false, items: [] };

export function useHomeLayout() {
  const { api, isAuthenticated } = useAuth();
  const [layout, setLayout] = useState<HomeLayout>(LEEG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) { setLoading(false); return; }
    setLoading(true);
    try {
      const res: any = await api.get('/api/v1/home-layout');
      setLayout({
        available: true,
        source: res?.source ?? null,
        personalised: res?.personalised === true,
        items: Array.isArray(res?.items) ? res.items : [],
      });
    } catch {
      // 404 (nog niet uitgerold), 403, netwerk — in alle gevallen: geen server-indeling.
      setLayout(LEEG);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  /**
   * Bewaar de persoonlijke laag.
   *
   * De server geeft de opgeloste indeling terug, en die zetten we meteen — anders kan
   * het scherm afwijken van wat er opgeslagen staat, bijvoorbeeld wanneer een lamp
   * inmiddels gesloten is en dus uit de lijst valt.
   */
  const save = useCallback(async (items: HomeLayoutItem[]) => {
    setSaving(true);
    try {
      const res: any = await api.put('/api/v1/home-layout', { items });
      setLayout({
        available: true,
        source: res?.source ?? null,
        personalised: true,
        items: Array.isArray(res?.items) ? res.items : [],
      });
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [api]);

  return { layout, loading, saving, reload: load, save };
}
